import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Project, User } from '../types';
import ProjectConfigActions from '../pages/project-detail/components/ProjectConfigActions';
import { projectEpisodePath } from '../utils/routes';
import { useIsMobile } from '../utils/useIsMobile';
import CostFloatingPill from './CostFloatingPill';
import { useProjectCommentDots } from './useProjectCommentDots';

interface HeaderProjectNavProps {
  projectId: string;
  active: 'assets' | 'script' | 'storyboard';
  // Target episode for the storyboard segment when on the assets page.
  // null/undefined => no episode available, segment disabled.
  storyboardEpisodeId?: string | null;
  // 顶栏「项目设置/风格设置/协作者」按钮：传入 project + 开弹窗回调即渲染（弹窗在页面里，§3-③ 外层）。
  project?: Project;
  currentUser?: User | null;
  memberCount?: number;
  onEditProject?: () => void;
  onStyleConfig?: () => void;
  // 硬强制未完成时为 true：隐藏页面切换 toggle + 「项目设置/风格设置/协作者」按钮，
  // 仅保留「全部项目」。避免用户绕过强制弹窗调出同系列弹窗，或经 toggle 进入其他被白底
  // 盖住、无内容可操作的项目页造成迷惑——但仍可经「全部项目」离开项目。
  forced?: boolean;
  // Step4「请上传剧本」态（forced 仍为 true）：前两步已完成，放出「项目设置/风格设置」两个按钮
  // 供回看修改，并在其下方悬挂一行提示气泡。toggle 仍隐藏。
  scriptStep?: boolean;
}

export default function HeaderProjectNav({
  projectId,
  active,
  storyboardEpisodeId,
  project,
  currentUser,
  memberCount,
  onEditProject,
  onStyleConfig,
  forced,
  scriptStep,
}: HeaderProjectNavProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // 资产/视频创作有未处理评论时，在对应 toggle 段上挂红点。
  const { assetHasPending, storyboardHasPending } = useProjectCommentDots(projectId);

  // Step4 顶栏「项目设置/风格设置」下方悬挂的提示气泡（单行不换行，顶部小三角指向按钮）。
  // 处于顶栏(z-index:100)层叠上下文内，自然浮在硬强制纯色遮罩(z-index:90)之上。
  const configHintBubble = (
    <div
      role="note"
      style={{
        position: 'absolute', top: 'calc(100% + 9px)', right: 0, zIndex: 1,
        display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
        background: 'var(--primary)', border: 'none', borderRadius: '8px',
        padding: '6px 12px', fontSize: '0.78rem', fontWeight: 500, color: '#fff',
        boxShadow: '0 6px 18px color-mix(in srgb, var(--primary) 40%, transparent)',
        animation: 'hintFloat 2.4s ease-in-out infinite',
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute', top: '-4px', right: '30px', width: '9px', height: '9px',
          background: 'var(--primary)', transform: 'rotate(45deg)',
        }}
      />
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
      {t('project.forcedConfigHint', { ns: 'projectDetail' })}
    </div>
  );

  const storyboardDisabled = active !== 'storyboard' && !storyboardEpisodeId;

  const segBase: React.CSSProperties = {
    position: 'relative',
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
    padding: '5px 16px', borderRadius: '8px', lineHeight: 1.4, whiteSpace: 'nowrap',
    transition: 'all 0.15s', background: 'transparent', color: 'var(--text-2)',
  };
  // 未处理评论红点：toggle 段右上角，纯红点。
  const commentDot = (
    <span
      aria-hidden
      style={{
        position: 'absolute', top: '3px', right: '6px',
        width: '7px', height: '7px', borderRadius: '50%',
        background: '#ef4444',
      }}
    />
  );
  const assetsActive: React.CSSProperties = {
    background: 'linear-gradient(135deg, var(--theme-project), #60a5fa)', color: '#fff',
    boxShadow: '0 2px 8px color-mix(in srgb, var(--theme-project) 45%, transparent)',
  };
  const scriptActive: React.CSSProperties = {
    background: 'linear-gradient(135deg, var(--theme-script), #34d399)', color: '#fff',
    boxShadow: '0 2px 8px color-mix(in srgb, var(--theme-script) 45%, transparent)',
  };
  const storyboardActive: React.CSSProperties = {
    background: 'linear-gradient(135deg, var(--theme-storyboard), #3b82f6)', color: '#fff',
    boxShadow: '0 2px 8px color-mix(in srgb, var(--theme-storyboard) 45%, transparent)',
  };
  // 整个 toggle 外描边用当前所在页的主题色渐变（加粗），让三段看起来是一个 toggle 整体而非三个独立按钮。
  const activeGradient =
    active === 'assets' ? 'linear-gradient(135deg, var(--theme-project), #60a5fa)'
    : active === 'script' ? 'linear-gradient(135deg, var(--theme-script), #34d399)'
    : 'linear-gradient(135deg, var(--theme-storyboard), #3b82f6)';

  const backButton = (
    <button
      onClick={() => navigate('/projects')}
      title={t('projectNav.allProjects')}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0,
        background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px',
        padding: '5px 10px', fontSize: '0.8rem', color: 'var(--text-2)',
        cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.color = 'var(--text)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)'; }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
      </svg>
      <span className="hnav-label">{t('projectNav.allProjects')}</span>
    </button>
  );

  // 页面切换 toggle（剧本信息 | 资产设定 | 视频创作）：硬强制态隐藏，避免经 toggle 进入被白底盖住、无内容可操作的其他项目页
  const toggle = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0,
      borderRadius: '10px', padding: '3px',
      border: '2px solid transparent',
      background: `linear-gradient(var(--surface), var(--surface)) padding-box, ${activeGradient} border-box`,
    }}>
      <button
        style={{ ...segBase, ...(active === 'script' ? scriptActive : {}) }}
        title={t('projectNav.scriptInfo')}
        onClick={() => { if (active !== 'script') navigate(`/projects/${projectId}/script-summary`); }}
        onMouseEnter={e => { if (active !== 'script') { e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.color = 'var(--text)'; } }}
        onMouseLeave={e => { if (active !== 'script') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)'; } }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
        </svg>
        <span className="hnav-label">{t('projectNav.scriptInfo')}</span>
      </button>
      <button
        style={{ ...segBase, ...(active === 'assets' ? assetsActive : {}) }}
        title={t('projectNav.assetHome')}
        onClick={() => { if (active !== 'assets') navigate(`/projects/${projectId}`); }}
        onMouseEnter={e => { if (active !== 'assets') { e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.color = 'var(--text)'; } }}
        onMouseLeave={e => { if (active !== 'assets') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)'; } }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" />
        </svg>
        <span className="hnav-label">{t('projectNav.assetHome')}</span>
        {assetHasPending && commentDot}
      </button>
      <button
        style={{ ...segBase, ...(active === 'storyboard' ? storyboardActive : {}), ...(storyboardDisabled ? { cursor: 'not-allowed', opacity: 0.45 } : {}) }}
        disabled={storyboardDisabled}
        onClick={() => { if (active !== 'storyboard' && storyboardEpisodeId) navigate(projectEpisodePath(projectId, storyboardEpisodeId)); }}
        onMouseEnter={e => { if (active !== 'storyboard' && !storyboardDisabled) { e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.color = 'var(--text)'; } }}
        onMouseLeave={e => { if (active !== 'storyboard' && !storyboardDisabled) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)'; } }}
        title={storyboardDisabled ? t('projectNav.noEpisodes') : t('projectNav.storyboard')}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
        </svg>
        <span className="hnav-label">{t('projectNav.storyboard')}</span>
        {storyboardHasPending && commentDot}
      </button>
    </div>
  );

  if (isMobile) {
    return (
      <div className="header-project-nav" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '0.4rem', minWidth: 0, width: '100%' }}>
        {/* 第一行：返回 / 本项目消耗 / flex 撑开 / 扳手（三项设置收进下拉） */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          {backButton}
          {project ? <CostFloatingPill projectId={projectId} variant="inline" /> : null}
          <div style={{ flex: 1 }} />
          {project && onEditProject && onStyleConfig && (!forced || scriptStep) && (
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <ProjectConfigActions
                project={project}
                currentUser={currentUser ?? null}
                memberCount={memberCount}
                onEditProject={onEditProject}
                onStyleConfig={onStyleConfig}
                compact
                settingsOnly={forced && scriptStep}
              />
              {forced && scriptStep && configHintBubble}
            </div>
          )}
        </div>
        {/* 第二行：页面切换 toggle，居中；硬强制态隐藏 */}
        {!forced && <div style={{ display: 'flex', justifyContent: 'center' }}>{toggle}</div>}
      </div>
    );
  }

  return (
    <div className="header-project-nav" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
      {backButton}

      {!forced && toggle}

      {project && onEditProject && onStyleConfig && (!forced || scriptStep) && (
        <div style={{ marginLeft: '1.25rem', flexShrink: 0, position: 'relative' }}>
          <ProjectConfigActions
            project={project}
            currentUser={currentUser ?? null}
            memberCount={memberCount}
            onEditProject={onEditProject}
            onStyleConfig={onStyleConfig}
            settingsOnly={forced && scriptStep}
          />
          {forced && scriptStep && configHintBubble}
        </div>
      )}
    </div>
  );
}
